import { Button, Popconfirm, Spin, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TABLE_MAX_HEIGHT } from '@/constants/customer'
import {
  GetWashingHistoryByDayResponse,
  WashingHistory,
} from '@/types/apis/washingHistory/GetWashingHistoryByDayResponse'
import { GetWashingHistoryByRangeDayResponse } from '@/types/apis/washingHistory/GetWashingHistoryByRangeDayResponse'
import { formatYMD } from '@/utils/date'
import { formatJapaneseNumber } from '@/utils/formatPrice'

import { CustomRowTable } from '../styles'

type Props = {
  responseWashingHistoryByRangeDay?: GetWashingHistoryByRangeDayResponse | undefined
  washingHistoryByDayResponseLoading?: boolean
  deleteLaundry: (id: number) => Promise<void>
  deleteLoading: boolean
  setIsReloadByRangeDate: React.Dispatch<React.SetStateAction<boolean>>
}

const WashingHistoryByRangeDayTable = ({
  responseWashingHistoryByRangeDay,
  deleteLaundry,
  setIsReloadByRangeDate,
}: Props) => {
  const { t } = useTranslation()
  const [visibleConfirmDelete, setVisibleConfirmDelete] = useState<Record<string, boolean>>({})

  const onDelete = async (id: number) => {
    setIsReloadByRangeDate(true)
    await deleteLaundry(id)
    setVisibleConfirmDelete({})
    setIsReloadByRangeDate(false)
  }

  const baseColumns: ColumnsType<WashingHistory> = [
    {
      title: t('components.pages.washingHistory.listReport.table.date'),
      key: 'date',
      width: 100,
      fixed: 'left',
      align: 'center',
      className: 'nowrap-tank',
      render: (record: WashingHistory) => {
        return <CustomRowTable type="left">{formatYMD(record.laundryStart)}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.operatorName'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 150,
      fixed: 'left',
      align: 'center',
      className: 'nowrap-tank',
      render: (text: string) => {
        return <CustomRowTable type="left">{text}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.washingMachineCode'),
      dataIndex: 'washingMachineId',
      key: 'washingMachineId',
      width: 120,
      fixed: 'left',
      align: 'center',
      className: 'nowrap-tank',
      render: (text: number) => {
        return <CustomRowTable type="right">{text}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.washingMachineName'),
      dataIndex: 'washingMachineName',
      key: 'washingMachineName',
      width: 150,
      fixed: 'left',
      align: 'center',
      className: 'nowrap-tank',
      render: (text: string) => {
        return <CustomRowTable type="left">{text}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.customerName'),
      dataIndex: 'customerShortName',
      key: 'customerShortName',
      width: 200,
      fixed: 'left',
      align: 'center',
      render: (text: string) => {
        return <CustomRowTable type="left">{text}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.washingMethod'),
      dataIndex: 'washingMethodName',
      key: 'washingMethodName',
      align: 'center',
      className: 'nowrap-tank',
      render: (washingMethodName: string) => {
        return <CustomRowTable type="left">{washingMethodName}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.laundryWeight'),
      dataIndex: 'volumes',
      key: 'volumes',
      align: 'center',
      className: 'nowrap-tank',
      render: (volumes: number) => {
        return <CustomRowTable type="right">{volumes}</CustomRowTable>
      },
    },
    {
      title: t('components.pages.washingHistory.listReport.table.detergentCost'),
      dataIndex: 'cost',
      key: 'cost',
      align: 'center',
      className: 'nowrap-tank',
      render: (cost: number) => {
        return <CustomRowTable type="right">{cost}</CustomRowTable>
      },
    },
  ]

  const detergentColumns: ColumnsType<WashingHistory> =
    responseWashingHistoryByRangeDay?.detergent.map((item) => ({
      title: item.name + '/ml',
      dataIndex: item.name,
      key: item.name,
      align: 'center',
      className: 'nowrap-export',
      render: (_: GetWashingHistoryByDayResponse, record: WashingHistory) => {
        const found = record.laundryDetergents.find((det) => det.name === item.name)
        return (
          <CustomRowTable type="right">
            {found && found.quantity ? formatJapaneseNumber(found.quantity) : ''}
          </CustomRowTable>
        )
      },
    })) || []

  const waterColumn: ColumnsType<WashingHistory> = [
    {
      title: t('components.pages.washingHistory.listReport.table.water'),
      dataIndex: 'waterQuantity',
      key: 'waterQuantity',
      align: 'center',
      className: 'nowrap-export',
      render: (waterQuantity: number) => {
        return <CustomRowTable type="right">{formatJapaneseNumber(waterQuantity)}</CustomRowTable>
      },
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      className: 'nowrap',
      render: (text: string, record: WashingHistory) => {
        return (
          <Popconfirm
            id={record.id.toString()}
            visible={!!visibleConfirmDelete[record.id]}
            title={t('components.pages.attendanceLogs.list.table.confirmDelete')}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            onConfirm={() => {
              onDelete(record.id)
            }}
            onCancel={() => setVisibleConfirmDelete({})}
          >
            <Button size="small" danger onClick={() => setVisibleConfirmDelete({ [record.id]: true })} loading={false}>
              {t('components.pages.admins.list.table.delete')}
            </Button>
          </Popconfirm>
        )
      },
    },
  ]

  const finalColumns = [...baseColumns, ...detergentColumns, ...waterColumn]

  const summaryDataBase = [
    {
      key: 1,
      title: t('components.pages.washingHistory.listReport.table.totalUsage'),
    },
    {
      key: 2,
      title: t('components.pages.washingHistory.listReport.table.usagePerCapacity'),
    },
    {
      key: 3,
      title: t('components.pages.washingHistory.listReport.table.amount'),
    },
  ]

  const firstItemExtended = responseWashingHistoryByRangeDay?.detergent.reduce((acc, item) => {
    const key = `${item.name}`
    return {
      ...acc,
      [key]: item.totalQuantity ? formatJapaneseNumber(item.totalQuantity) : '',
      ['waterQuantity']: responseWashingHistoryByRangeDay.totalWaterQuantity
        ? formatJapaneseNumber(responseWashingHistoryByRangeDay.totalWaterQuantity)
        : '',
    }
  }, summaryDataBase[0])

  const secondItemExtended = responseWashingHistoryByRangeDay?.detergent.reduce((acc, item) => {
    const key = `${item.name}`
    return {
      ...acc,
      [key]: item.totalSpecificGravity ? formatJapaneseNumber(item.totalSpecificGravity) : '',
      ['waterQuantity']: responseWashingHistoryByRangeDay.totalWaterSpecificGravity
        ? formatJapaneseNumber(responseWashingHistoryByRangeDay.totalWaterSpecificGravity)
        : '',
    }
  }, summaryDataBase[1])

  const thirdItemExtended = responseWashingHistoryByRangeDay?.detergent.reduce((acc, item) => {
    const key = `${item.name}`
    return {
      ...acc,
      [key]: item.totalPrice ? formatJapaneseNumber(item.totalPrice) : '',
      ['cost']: responseWashingHistoryByRangeDay?.totalDetergentPrice
        ? formatJapaneseNumber(responseWashingHistoryByRangeDay?.totalDetergentPrice)
        : '',
      ['waterQuantity']: responseWashingHistoryByRangeDay.totalWaterPrice
        ? formatJapaneseNumber(responseWashingHistoryByRangeDay.totalWaterPrice)
        : '',
    }
  }, summaryDataBase[2])

  const summaryData = [firstItemExtended, secondItemExtended, thirdItemExtended]

  return (
    <Spin spinning={false}>
      <Table
        columns={finalColumns}
        dataSource={responseWashingHistoryByRangeDay?.items}
        bordered
        pagination={false}
        scroll={{ x: 'max-content', y: TABLE_MAX_HEIGHT }}
        summary={() => (
          <>
            {summaryData.map((row) => (
              <Table.Summary.Row key={row?.key}>
                {finalColumns.map((col, idx) => {
                  if (idx === 4) {
                    return (
                      <Table.Summary.Cell key={col.key} index={idx}>
                        <strong>{row?.title}</strong>
                      </Table.Summary.Cell>
                    )
                  }

                  const value = row ? row[col.key as keyof typeof row] : ''
                  return (
                    <Table.Summary.Cell key={col.key} index={idx}>
                      <CustomRowTable type="right">{value}</CustomRowTable>
                    </Table.Summary.Cell>
                  )
                })}
              </Table.Summary.Row>
            ))}
          </>
        )}
      />
    </Spin>
  )
}

WashingHistoryByRangeDayTable.displayName = 'WashingHistoryByRangeDayTable'
export default WashingHistoryByRangeDayTable
